import React, { useEffect, useState } from "react";
import logo from '../../assets/images/CoreVentum_logo_dark.png';
import img1 from "../../assets/images/img1.png";
import img2 from "../../assets/images/img2.png";
import axiosInstance from "../../utils/axiosInstance";
import SuccessCard from "../../components/messages/SuccessCard";
import BookingConfirmation from "../../components/DemoRequest/BookingConfirmation";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { message, Button, Card, Select, Calendar, List } from "antd";
import { LeftOutlined } from "@ant-design/icons";

dayjs.extend(utc);
dayjs.extend(timezone);

const timezones = ["Asia/Kolkata", "Asia/Dubai"];

const generateAvailableSlots = (timezone = "Asia/Kolkata") => {
  const slots = {};
  const startHour = 10;
  const endHour = 18;

  for (let i = 0; i < 30; i++) {
    const date = dayjs().tz(timezone).add(i, "day");
    const dayOfWeek = date.day();
    if (dayOfWeek >= 1 && dayOfWeek <= 5) {
      const formattedDate = date.format("YYYY-MM-DD");
      slots[formattedDate] = [];
      for (let hour = startHour; hour < endHour; hour++) {
        slots[formattedDate].push(`${hour.toString().padStart(2, "0")}:00`);
        slots[formattedDate].push(`${hour.toString().padStart(2, "0")}:30`);
      }
    }
  }
  return slots;
};

const convertTime = (time, date, selectedTimezone, localTimeZone) => {
  const localDateTime = dayjs.tz(`${date} ${time}`, "YYYY-MM-DD HH:mm", localTimeZone).tz(selectedTimezone);
  return { adjustedDate: localDateTime.format("YYYY-MM-DD"), adjustedTime: localDateTime.format("HH:mm") };
};

export default function DemoScheduler() {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [availableData, setAvailableData] = useState({});
  const [bookedSlots, setBookedSlots] = useState({});
  const [selectedTimezone, setSelectedTimezone] = useState("Asia/Kolkata");
  const [timezoneAdjustedData, setTimezoneAdjustedData] = useState({});
  const [timezoneAdjustedUnavailable, setTimezoneAdjustedUnavailable] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);

  const fetchUnavailableSlots = async () => {
    try {
      const response = await axiosInstance.get("/demo/busy-slots/");
      setBookedSlots(response.data.blocked_slots || {});
    } catch (error) {
      message.error(error?.response?.data?.error || "Error fetching slots");
    }
  };

  useEffect(() => {
    fetchUnavailableSlots();
  }, []);

  // Update adjusted slots when timezone changes
  useEffect(() => {
    const rawAvailable = generateAvailableSlots(selectedTimezone);
    const adjustSlots = (data) => {
      const adjusted = {};
      Object.keys(data).forEach((date) => {
        data[date].forEach((time) => {
          const { adjustedDate, adjustedTime } = convertTime(time, date, selectedTimezone, "Asia/Kolkata");
          if (!adjusted[adjustedDate]) adjusted[adjustedDate] = [];
          adjusted[adjustedDate].push(adjustedTime);
        });
      });
      return adjusted;
    };

    setTimezoneAdjustedData(adjustSlots(rawAvailable));
    setTimezoneAdjustedUnavailable(adjustSlots(bookedSlots));
  }, [selectedTimezone, availableData, bookedSlots]);

  const isPast = (date, time) =>
    dayjs.tz(`${date} ${time}`, "YYYY-MM-DD HH:mm", selectedTimezone).isBefore(dayjs().tz(selectedTimezone));

  const getFormattedDate = (date) => {
    if (!date) return "";
    const dayjsDate = dayjs(date);
    const day = dayjsDate.date();
    const suffix = day > 3 && day < 21 ? "th" : ["th", "st", "nd", "rd"][day % 10] || "th";
    return `${dayjsDate.format("dddd, MMMM")} ${day}${suffix} ${dayjsDate.format("YYYY")}`;
  };

  const dateTime = selectedDate && selectedTime ? dayjs(`${selectedDate} ${selectedTime}`).format("YYYY-MM-DDTHH:mm") : null;

  return (
    <div
      className="h-screen w-full bg-cover bg-center relative flex"
      style={{ backgroundImage: `url(${img2})` }}
    >
      <nav className="pl-3 pt-4">
        <img src={logo} alt="Logo" width={180} height={150} />
      </nav>

      <div className="absolute top-2 left-[15%] lg:w-[40%] xl:w-[45%]">
        <h1 className="text-white font-extrabold text-[18px] my-2 text-center">
          Contract Life-Cycle Management Software (CLMS)
        </h1>
        <img src={img1} alt="Top Right" />
      </div>

      <div className="absolute bottom-16 right-14 text-white w-full max-w-[450px] z-20">
        <div className="glass-card shadow-lg rounded-lg p-6 w-full max-w-lg h-full">
          {/* Timezone Selector */}
          <div className="flex justify-center mb-4">
            <Select
              value={selectedTimezone}
              onChange={setSelectedTimezone}
              style={{ width: 180 }}
              className="glass-card"
            >
              {timezones.map((tz) => (
                <Select.Option key={tz} value={tz}>
                  {tz}
                </Select.Option>
              ))}
            </Select>
          </div>

          {/* Calendar for Date Selection */}
          {!selectedDate && (
            <div>
              <h3 className="text-center text-lg font-medium mb-2">Select Day</h3>
              <Card className="glass-card mb-4">
                <Calendar
                  fullscreen={false}
                  disabledDate={(date) => !timezoneAdjustedData[date.format("YYYY-MM-DD")]}
                  onSelect={(date) => setSelectedDate(date.format("YYYY-MM-DD"))}
                  dateCellRender={(value) =>
                    timezoneAdjustedData[value.format("YYYY-MM-DD")] ? null : (
                      <div style={{ color: "#bbb" }}></div>
                    )
                  }
                />
              </Card>
            </div>
          )}

          {/* Time Slots */}
          {selectedDate && !selectedTime && (
            <div>
              <div className="flex justify-between mb-2">
                <Button onClick={() => setSelectedDate(null)}>
                  <LeftOutlined /> Back
                </Button>
                <span>{getFormattedDate(selectedDate)}</span>
              </div>
              <div className="flex flex-col gap-2 h-[430px] overflow-y-scroll">
                {timezoneAdjustedData[selectedDate]?.map((time) => (
                  <Button
                    key={time}
                    type="primary"
                    block
                    disabled={timezoneAdjustedUnavailable[selectedDate]?.includes(time) || isPast(selectedDate, time)}
                    style={{
                      backgroundColor:
                        timezoneAdjustedUnavailable[selectedDate]?.includes(time) || isPast(selectedDate, time)
                          ? "transparent"
                          : "#122253",
                      borderColor: "#122253",
                      color:
                        timezoneAdjustedUnavailable[selectedDate]?.includes(time) || isPast(selectedDate, time)
                          ? "#000"
                          : "#fff"
                    }}
                    onClick={() => setSelectedTime(time)}
                  >
                    {timezoneAdjustedUnavailable[selectedDate]?.includes(time) ? `Not Available ${time}` : `Book ${time}`}
                  </Button>
                )) || <div>No available slots</div>}
              </div>
            </div>
          )}

          {/* Booking Confirmation */}
          {selectedDate && selectedTime && (
            <BookingConfirmation
              selectedDate={selectedDate}
              selectedTime={selectedTime}
              goBack={() => setSelectedTime(null)}
              dateTime={dateTime}
              getFormattedDate={getFormattedDate}
              onSuccess={() => setShowSuccess(true)}
            />
          )}
        </div>
      </div>

      <SuccessCard
        message="Your Demo Request Submitted"
        isVisible={showSuccess}
        onClose={() => setShowSuccess(false)}
      />
    </div>
  );
}

